import {
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Skeleton,
} from '@mui/material';
import React, { useState } from 'react';
import { Plus, PencilSimple, TrashSimple } from '@phosphor-icons/react';
import WebHookFunctionDrawer from './WebHookFunctionDrawer';
import { useDashboard } from '../../../hooks/useDashboard';
import { useDispatch } from 'react-redux';
import {
  setFunctionName,
  setFunctionDescription,
  addParameter,
  setApiEndpoint,
  setApiMethod,
  setProcessingMessage,
  addQueryParam,
  addParameterMapping,
  addResultMapping,
  resetFunctionBuilder,
  FunctionParameter,
} from '../../../utils/redux/slices/webhookFunctionSlice';
import { deleteWebhookFunctionApi } from '../../../utils/apis';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert } from '../../../hooks/useAlert';
import DeleteModal from '../../modals/DeleteModal';
import MessagePopup from '../../common/MessagePopup';
import { useAgents } from '../../../hooks/useAgents';

const WebHook: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const { isLoading } = useAgents();
  const { selectedAssistant } = useDashboard();
  const webhooks = selectedAssistant?.functionconfig?.webhook || [];
  const [editingWebhook, setEditingWebhook] = React.useState<any>(null);
  const dispatch = useDispatch();

  // New state for delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<any>(null);
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();

  // Delete webhook mutation
  const { mutate: deleteWebhook, isPending: isDeletePending } = useMutation({
    mutationFn: (webhook: any) =>
      deleteWebhookFunctionApi(selectedAssistant?._id || '', webhook.name),
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      showAlert('Webhook function deleted successfully', 'success');
    },
    onError: error => {
      console.error('Error deleting webhook:', error);
      showAlert('Error deleting webhook function', 'error');
    },
  });

  const handleEditWebhook = (webhook: any) => {
    // Reset the form first
    dispatch(resetFunctionBuilder());

    // Ensure we're working with the latest data - add this line
    const latestWebhooks = selectedAssistant?.functionconfig?.webhook || [];
    const latestWebhook =
      latestWebhooks.find((w: any) => w.name === webhook.name) || webhook;

    // Set the editing webhook - use the latest data
    setEditingWebhook(latestWebhook);

    // Fill the form with the webhook data - use latestWebhook instead of webhook
    // Basic info
    dispatch(setFunctionName(latestWebhook.name));
    dispatch(setFunctionDescription(latestWebhook.description));

    // Parameters
    const properties = latestWebhook.parameters?.properties || {};
    const requiredParams = latestWebhook.parameters?.required || [];

    Object.entries(properties).forEach(([name, paramData]: [string, any]) => {
      // Create a base parameter
      const parameter: FunctionParameter = {
        name,
        type: paramData.type,
        description: paramData.description,
        required: requiredParams.includes(name),
      };

      // For array type, add the arrayItems property correctly
      if (paramData.type === 'array') {
        // Find the actual object field name inside the array parameter
        const customItemKeys = Object.keys(paramData).filter(
          key => key !== 'type' && key !== 'description'
        );

        // Look for object definition that might be a custom item name
        const itemsKey = customItemKeys.find(
          key =>
            typeof paramData[key] === 'object' &&
            paramData[key]?.type === 'object'
        );

        if (itemsKey) {
          // Use the actual name from the structure
          parameter.arrayItems = {
            type: 'object',
            itemsName: itemsKey, // This will be "user" instead of appending "item"
            properties: {},
            required: paramData[itemsKey]?.required || [],
          };

          // Add the properties to the arrayItems
          if (paramData[itemsKey]?.properties) {
            Object.entries(paramData[itemsKey].properties).forEach(
              ([propName, propDetails]: [string, any]) => {
                if (parameter.arrayItems && parameter.arrayItems.properties) {
                  parameter.arrayItems.properties[propName] = {
                    type: propDetails.type,
                    description: propDetails.description || '',
                  };
                }
              }
            );
          }
        }
      }

      dispatch(addParameter(parameter));
    });

    // API Config
    dispatch(setApiEndpoint(latestWebhook.api_config.endpoint));
    dispatch(setApiMethod(latestWebhook.api_config.method));
    dispatch(
      setProcessingMessage(latestWebhook.api_config.processing_message || '')
    );

    // Fixed params (API Key)
    const fixedParams = latestWebhook.api_config.fixed_params || {};
    Object.entries(fixedParams).forEach(([key, value]: [string, any]) => {
      dispatch(addQueryParam({ key, value }));
    });

    // Parameter mappings
    const paramMappings = latestWebhook.api_config.parameter_mappings || [];
    paramMappings.forEach((mapping: any) => {
      dispatch(
        addParameterMapping({
          functionParameter: mapping.source,
          apiParameter: mapping.target,
          location: mapping.location,
          value: mapping.value,
        })
      );
    });

    // Result mappings
    const resultMapping = latestWebhook.api_config.result_mapping;

    // Handle different result_mapping formats
    if (resultMapping && typeof resultMapping === 'object') {
      // Extract key-value pairs from the object
      Object.keys(resultMapping).forEach(key => {
        const value = resultMapping[key];
        dispatch(
          addResultMapping({
            resultField: key,
            apiResponsePath: value,
          })
        );
      });
    }

    // Open the drawer
    setOpen(true);
  };

  const handleDeleteWebhook = (webhook: any) => {
    setWebhookToDelete(webhook);
    setIsDeleteModalOpen(true);
  };

  // Handle actual deletion
  const handleConfirmDelete = () => {
    if (webhookToDelete) {
      deleteWebhook(webhookToDelete);
    }
  };

  const renderWebhookList = () => {
    return (
      <Box
        width="100%"
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {webhooks.map((webhook: any, index: number) => (
          <Paper
            key={webhook.name || index}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '16px',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                <Typography sx={{ fontWeight: 500 }}>{webhook.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {webhook.description}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <IconButton
                  onClick={() => handleEditWebhook(webhook)}
                  size="small"
                >
                  <PencilSimple size={18} />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteWebhook(webhook)}
                  size="small"
                >
                  <TrashSimple size={18} weight="fill" color="#E74C3C" />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderEmptyState = () => {
    return (
      <>
        <Typography
          sx={{ fontSize: '14px', fontWeight: '400', color: '#6B6F80' }}
        >
          No Webhook Functions created yet. Create your first function to get
          started
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus size={16} />}
          onClick={() => {
            dispatch(resetFunctionBuilder());
            setEditingWebhook(null);
            setOpen(true);
          }}
          sx={{
            width: 'fit-content',
            height: '34px',
            borderRadius: '6px',
            textTransform: 'none',
            boxShadow: 'none',
            padding: '8px 16px',
          }}
        >
          Create Function
        </Button>
      </>
    );
  };

  const renderWebhookListSkeleton = () => {
    return (
      <Box
        width="100%"
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {[...Array(3)].map((_, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                <Skeleton variant="text" width={150} height={24} />
                <Skeleton variant="text" width={250} height={20} />
              </Box>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Skeleton variant="circular" width={30} height={30} />
                <Skeleton variant="circular" width={30} height={30} />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        height="100%"
        gap="16px"
        width="100%"
      >
        {isLoading ? (
          renderWebhookListSkeleton()
        ) : webhooks.length > 0 ? (
          renderWebhookList()
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            height="100%"
            gap="16px"
            width="100%"
            py="16px"
            justifyContent="center"
          >
            {renderEmptyState()}
          </Box>
        )}
        <WebHookFunctionDrawer
          open={open}
          onClose={() => setOpen(false)}
          isEditing={!!editingWebhook}
          webhookData={editingWebhook}
        />

        {/* Add the DeleteModal */}
        <DeleteModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleConfirmDelete}
          isPending={isDeletePending}
          title="Delete Webhook Function"
          description="Are you sure you want to delete this webhook function? This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
        />
        <MessagePopup alert={alert} setAlert={setAlert} />
      </Box>
    </>
  );
};

export default WebHook;
